export function lightenColor(color: string, percentage: number) {
    if (color === undefined) {
        return "#FFFFFF";
      }
    percentage = Math.max(0, Math.min(100, percentage));

    const normalizedColor = normalizeColor(color);

    // Convert to appropriate color space based on input type
    switch (normalizedColor.type) {
        case 'hex':
            return lightenHex(normalizedColor.color, percentage);
        case 'rgb':
        case 'rgba':
            return lightenRGB(normalizedColor.color, percentage, normalizedColor.type);
        case 'hsl':
        case 'hsla':
            return lightenHSL(normalizedColor.color, percentage, normalizedColor.type);
        default:
            throw new Error('Unsupported color format');
    }
}

function normalizeColor(color: string) {
    color = color.trim().toLowerCase();

    if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(color)) {
        if (color.length === 4 || color.length === 5) {
            color = color.split('').map((char, index) => 
                index === 0 ? '#' : char + char
            ).join('');
        }
        return { type: 'hex', color };
    }

    const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
    if (rgbMatch) {
        return { 
            type: rgbMatch[4] !== undefined ? 'rgba' : 'rgb', 
            color: rgbMatch[0] 
        };
    }

    const hslMatch = color.match(/^hsla?\((\d+),\s*(\d+)%,\s*(\d+)%(?:,\s*([\d.]+))?\)$/);
    if (hslMatch) {
        return { 
            type: hslMatch[4] !== undefined ? 'hsla' : 'hsl', 
            color: hslMatch[0] 
        };
    }

    throw new Error('Unsupported color format');
}

function lightenHex(hex: string, percentage: number) {
    hex = hex.replace('#', '');

    const alpha = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    const lightened = [r, g, b].map(color => 
        Math.round(color + (255 - color) * (percentage / 100))
    );

    const lightenedHex = '#' + lightened.map(c => 
        c.toString(16).padStart(2, '0')
    ).join('');

    return hex.length === 8 
        ? lightenedHex + (alpha * 255).toString(16).padStart(2, '0')
        : lightenedHex;
}

function lightenRGB(rgb: string, percentage: number, type: string) {
    const rgbValues = (rgb.match(/\d+(\.\d+)?/g) ?? []).map(Number);
    
    const lightened = rgbValues.slice(0, 3).map(color => 
        Math.round(color + (255 - color) * (percentage / 100))
    );

    return type === 'rgba' 
        ? `rgba(${lightened[0]}, ${lightened[1]}, ${lightened[2]}, ${rgbValues[3]})` 
        : `rgb(${lightened[0]}, ${lightened[1]}, ${lightened[2]})`;
}

function lightenHSL(hsl: string, percentage: number, type: string) {
    const values = (hsl.match(/\d+(\.\d+)?/g) ?? []).map(Number);
    
    const newLightness = Math.min(100, values[2] + percentage);

    return type === 'hsla'
        ? `hsla(${values[0]}, ${values[1]}%, ${newLightness}%, ${values[3]})`
        : `hsl(${values[0]}, ${values[1]}%, ${newLightness}%)`;
}
